import { ReactLazyPreload } from '@creator/ui/utils/react';
import { removeTrailingSlashes } from '@creator/ui/utils/url-utils';
import type { FC, PropsWithChildren } from 'react';
import { lazy, memo, Suspense, useEffect } from 'react';
import { Route, Routes } from 'react-router';
import AuthRequiredRoute from './components/Auth/AuthRequiredRoute/AuthRequiredRoute';
import NetworkAdminRequiredRoute from './components/Auth/NetworkAdminRequiredRoute/NetworkAdminRequiredRoute';
import NoAuthRequiredRoute from './components/Auth/NoAuthRequiredRoute/NoAuthRequiredRoute';
import { getUrlMapping } from './model/config/helpers';
import TokenPage from './pages/TokenPage/TokenPage';

const BbsDirectoryPage = ReactLazyPreload(() => import('./pages/BbsDirectoryPage/BbsDirectoryPage'));
const CreateAccountPage = ReactLazyPreload(() => import('./pages/CreateAccountPage/CreateAccountPage'));
const LoginPage = ReactLazyPreload(() => import('./pages/Login/Login'));
const SignInWithEmailLinkPage = ReactLazyPreload(() => import('./pages/SignInWithEmailLink/SignInWithEmailLink'));
const FiatInSuccessPage = ReactLazyPreload(() => import('./pages/FiatInSuccess/FiatInSuccess'));
const CreateTokenPage = ReactLazyPreload(() => import('./pages/CreateTokenPage/CreateTokenPage'));
const ChangePasswordWithEmailLinkPage = ReactLazyPreload(() => import('./pages/ChangePasswordWithEmailLink/ChangePasswordWithEmailLink'));
const NotificationsPage = ReactLazyPreload(() => import('./pages/NotificationsPage/NotificationsPage'));
const NotFoundPage = ReactLazyPreload(() => import('./pages/NotFoundPage/NotFoundPage'));
const UserPageById = ReactLazyPreload(() => import('./pages/UserPage/UserPageById'));
const SearchPage = ReactLazyPreload(() => import('./pages/SearchPage/SearchPage'));
const AccountSettingsPage = ReactLazyPreload(() => import('./pages/AccountSettingsPage/AccountSettingsPage'));
const ForumPickerPage = ReactLazyPreload(() => import('./pages/ForumPickerPage/ForumPickerPage'));
const NotificationSettingsPage = ReactLazyPreload(() => import('./pages/NotificationSettingsPage/NotificationSettingsPage'));
const DirectMessagePage = ReactLazyPreload(() => import('./pages/DirectMessagePage/DirectMessagePage'));
const CreateExternalArticleCommentPage = ReactLazyPreload(() => import('./pages/CreateExternalArticleCommentPage/CreateExternalArticleCommentPage'));
const UserPage = ReactLazyPreload(() => import('./pages/UserPage/UserPage'));
const CreatePostPage = ReactLazyPreload(() => import('./pages/CreatePostPage/CreatePostPage'));
const CreateAnonymousPostPage = ReactLazyPreload(() => import('./pages/CreatePostPage/CreateAnonymousPostPage'));
const AdminSettingsPage = ReactLazyPreload(() => import('./pages/AdminSettingsPage/AdminSettingsPage'));
const DomainMainMenuEditorPage = ReactLazyPreload(() => import('./pages/NetworkAdminDashboard/DomainMainMenuEditorPage/DomainMainMenuEditorPage'));
const DomainConfigurationPage = ReactLazyPreload(() => import('./pages/NetworkAdminDashboard/DomainConfigurationPage/DomainConfigurationPage'));
const NetworkAdminDashboard = ReactLazyPreload(() => import('./pages/NetworkAdminDashboard/NetworkAdminDashboard'));

const AppRoutes: FC<PropsWithChildren> = () => {
    useEffect(() => {
        // Preload critical pages when browser is idle
        window.setTimeout(() => {
            console.log('Preloading critical pages...');
            void Promise.all([
                BbsDirectoryPage.preload(),
                CreateAccountPage.preload(),
                LoginPage.preload(),
                SignInWithEmailLinkPage.preload(),
                FiatInSuccessPage.preload(),
                CreateTokenPage.preload(),
                ChangePasswordWithEmailLinkPage.preload(),
                NotificationsPage.preload(),
                NotFoundPage.preload(),
                UserPageById.preload(),
                SearchPage.preload(),
                AccountSettingsPage.preload(),
                ForumPickerPage.preload(),
                NotificationSettingsPage.preload(),
            ]);
        }, 1000);
    }, []);

    // Helper function to create nested routes for a token
    function createTokenRoutes(tokenName?: string) {
        return (
            <Routes>
                {/* Most specific routes first */}
                <Route
                    path="admin-settings/:tab?"
                    element={
                        <AuthRequiredRoute>
                            <AdminSettingsPage tokenName={tokenName} />
                        </AuthRequiredRoute>
                    }
                />
                <Route path="tab/:tab" element={<TokenPage tokenName={tokenName} />} />
                <Route path="video/:videoIdentifier" element={<TokenPage tokenName={tokenName} />} />
                <Route path="author/:slugOrDisplayName/message" element={<DirectMessagePage tokenName={tokenName} />} />
                <Route path="author/:slugOrDisplayName/:tab?" element={<UserPage tokenName={tokenName} />} />

                <Route path="community/:identifier" element={<TokenPage tokenName={tokenName} />} />
                <Route path="community/:flairId/:identifier" element={<TokenPage tokenName={tokenName} />} />
                <Route path="community/:identifier/comment/:commentId/:reply?/:replyId?" element={<TokenPage tokenName={tokenName} />} />
                <Route path="community/:flairId/:identifier/comment/:commentId/:reply?/:replyId?" element={<TokenPage tokenName={tokenName} />} />

                <Route path=":identifier" element={<TokenPage tokenName={tokenName} />} />
                <Route path=":flairId/:identifier" element={<TokenPage tokenName={tokenName} />} />
                <Route path=":identifier/comment/:commentId/:reply?/:replyId?" element={<TokenPage tokenName={tokenName} />} />
                <Route path=":flairId/:identifier/comment/:commentId/:reply?/:replyId?" element={<TokenPage tokenName={tokenName} />} />

                {/* General routes */}
                <Route path="" element={<TokenPage tokenName={tokenName} />} />
            </Routes>
        );
    }

    function renderTokenRoutes() {
        const urlMapping = getUrlMapping();
        const routes: React.ReactElement[] = [];

        // Create specific routes for tokens with sections and urlSlugs
        if (urlMapping && 'tokens' in urlMapping && urlMapping.tokens) {
            Object.entries(urlMapping.tokens).forEach(([tokenName, tokenConfig]) => {
                // Type guard to ensure tokenConfig has the expected properties
                if (typeof tokenConfig === 'object' && tokenConfig !== null) {
                    const config = tokenConfig as { urlSlug?: string; section?: string };

                    if (config.section && config.urlSlug) {
                        // Route for tokens with sections: /section/urlSlug/*
                        routes.push(<Route key={`${config.section}-${config.urlSlug}`} path={`${config.section}/${config.urlSlug}/*`} element={createTokenRoutes(tokenName)} />);
                    } else if (config.urlSlug && !config.section) {
                        // Route for tokens without sections: /urlSlug/*
                        routes.push(<Route key={config.urlSlug} path={`${config.urlSlug}/*`} element={createTokenRoutes(tokenName)} />);
                    }
                }
            });
        }

        // Fallback catch-all route for any remaining paths
        routes.push(<Route key="catch-all" path={`/:tokenSlugOrName/*`} element={createTokenRoutes()} />);

        return routes;
    }

    return (
        // <Suspense fallback={null}>
        <Routes>
            <Route path="/" element={<TokenPage />} />
            <Route path="/bbs" element={<BbsDirectoryPage />} />
            <Route
                path="/bbs/create-token"
                element={
                    <AuthRequiredRoute>
                        <CreateTokenPage />
                    </AuthRequiredRoute>
                }
            />
            <Route path="/bbs/search/:type?" element={<SearchPage />} />
            <Route path="/bbs/:tokenSlugOrName/search/:type?" element={<SearchPage />} />
            <Route path="/bbs/404" element={<NotFoundPage />} />
            <Route path="/bbs/picker" element={<ForumPickerPage />} />
            <Route path="/bbs/email-sign-in" element={<SignInWithEmailLinkPage />} />
            <Route path="/bbs/email-change-password" element={<ChangePasswordWithEmailLinkPage />} />
            <Route
                path="/bbs/my-account/settings"
                element={
                    <AuthRequiredRoute>
                        <AccountSettingsPage />
                    </AuthRequiredRoute>
                }
            />
            <Route path="/author/:id" element={<UserPageById />} />
            <Route path="/bbs/singup" element={<CreateAccountPage />} />
            <Route
                path="/bbs/login"
                element={
                    <NoAuthRequiredRoute>
                        <LoginPage />
                    </NoAuthRequiredRoute>
                }
            />
            <Route path="/bbs/fiat-in-success" element={<FiatInSuccessPage />} />
            <Route
                path="/bbs/notifications/:tab?"
                element={
                    <AuthRequiredRoute>
                        <NotificationsPage />
                    </AuthRequiredRoute>
                }
            />
            <Route
                path="/bbs/my-profile/settings/notifications"
                element={
                    <AuthRequiredRoute>
                        <NotificationSettingsPage />
                    </AuthRequiredRoute>
                }
            />
            <Route
                path="/bbs/:tokenSlugOrName/posts/create/:postId?"
                element={
                    <AuthRequiredRoute>
                        <CreatePostPage />
                    </AuthRequiredRoute>
                }
            />
            <Route path="/bbs/:tokenSlugOrName/posts/create/anonymous" element={<CreateAnonymousPostPage />} />
            <Route path="/bbs/external-article-comment" element={<CreateExternalArticleCommentPage />} />
            <Route
                path="/bbs/network-admin-dashboard"
                element={
                    <NetworkAdminRequiredRoute>
                        <NetworkAdminDashboard />
                    </NetworkAdminRequiredRoute>
                }
            />
            <Route
                path="/bbs/network-admin-dashboard/domain-main-menu-editor"
                element={
                    <NetworkAdminRequiredRoute>
                        <DomainMainMenuEditorPage />
                    </NetworkAdminRequiredRoute>
                }
            />
            <Route
                path="/bbs/network-admin-dashboard/domain-configuration"
                element={
                    <NetworkAdminRequiredRoute>
                        <DomainConfigurationPage />
                    </NetworkAdminRequiredRoute>
                }
            />
            {renderTokenRoutes()}

            {/* Fallback route */}
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
        // </Suspense>
    );
};

export default memo(AppRoutes);
